import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  Search as SearchIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon,
  Send as SendIcon,
  LocalShipping as DeliveryIcon,
  Receipt as BillingIcon,
  Security as ValidationIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import api from '../../services/api';

const MessageTrace = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [traceDetails, setTraceDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [traceDialogOpen, setTraceDialogOpen] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/admin/smpp/trace/statistics?hours=24');
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // If it looks like a single message ID, trace directly
      if (searchTerm.includes('-') && searchTerm.length === 36) {
        await traceMessage(searchTerm);
      } else {
        // Otherwise search
        const response = await api.get(`/admin/smpp/trace/search?q=${encodeURIComponent(searchTerm)}&type=${searchType}&limit=100`);
        setSearchResults(response.data.data || []);
        
        // If only one result, auto-select it
        if (response.data.data && response.data.data.length === 1) {
          await traceMessage(response.data.data[0].message_id);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const traceMessage = async (messageId) => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/smpp/trace/${messageId}`);
      setTraceDetails(response.data.data);
      setTraceDialogOpen(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to trace message');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Show success notification
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED': return 'success';
      case 'FAILED': return 'error';
      case 'PENDING': return 'warning';
      case 'SUBMIT_FAILED': return 'error';
      case 'VALIDATION_FAILED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DELIVERED': return <CheckCircleIcon color="success" />;
      case 'FAILED': return <ErrorIcon color="error" />;
      case 'PENDING': return <WarningIcon color="warning" />;
      default: return <InfoIcon />;
    }
  };

  const getTimelineIcon = (eventName, status) => {
    switch (eventName) {
      case 'Ingested': return <SendIcon />;
      case 'Validated': return <ValidationIcon />;
      case 'Routed': return <RouteIcon />;
      case 'Submitted': return <SendIcon />;
      case 'DLR Received': return <DeliveryIcon />;
      case 'Billed': return <BillingIcon />;
      default: return <InfoIcon />;
    }
  };

  const getTimelineColor = (status) => {
    switch (status) {
      case 'OK': return 'success';
      case 'FAIL': return 'error';
      case 'WARN': return 'warning';
      default: return 'grey';
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">SMPP Message Trace</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchStatistics}
        >
          Refresh Stats
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Search by Message ID, Campaign ID, or MSISDN. Auto-detection will identify the search type.
          Only SMPP messages are traced in this view.
        </Typography>
      </Alert>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Messages
                </Typography>
                <Typography variant="h5">
                  {statistics.total_messages || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Delivered
                </Typography>
                <Typography variant="h5" color="success.main">
                  {statistics.delivered || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="h5" color="error.main">
                  {statistics.failed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {statistics.pending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Submit Latency
                </Typography>
                <Typography variant="h5">
                  {Math.round(statistics.avg_submit_latency || 0)}ms
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Delivery Latency
                </Typography>
                <Typography variant="h5">
                  {Math.round(statistics.avg_delivery_latency || 0)}ms
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Search Messages</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Search (Message ID / Campaign ID / MSISDN)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter search term..."
              disabled={loading}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading && <LinearProgress />}
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Results ({searchResults.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Message ID</TableCell>
                    <TableCell>MSISDN</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Operator</TableCell>
                    <TableCell>DLR Time</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((result) => (
                    <TableRow key={result.message_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {result.message_id.substring(0, 8)}...
                          </Typography>
                          <Tooltip title="Copy Message ID">
                            <IconButton size="small" onClick={() => handleCopyToClipboard(result.message_id)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {result.msisdn.substring(0, 6)}****{result.msisdn.slice(-2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(result.final_status)}
                          <Chip
                            label={result.final_status}
                            color={getStatusColor(result.final_status)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(result.ingest_timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{result.route_name}</TableCell>
                      <TableCell>{result.operator_name}</TableCell>
                      <TableCell>
                        {result.dlr_time ? 
                          new Date(result.dlr_time).toLocaleString() : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Full Trace">
                          <IconButton size="small" onClick={() => traceMessage(result.message_id)}>
                            <TimelineIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Trace Details Dialog */}
      <Dialog 
        open={traceDialogOpen} 
        onClose={() => setTraceDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            Message Trace Details
            <Badge badgeContent={traceDetails?.final_status} color={getStatusColor(traceDetails?.final_status)}>
              <Avatar>
                {getStatusIcon(traceDetails?.final_status)}
              </Avatar>
            </Badge>
          </Box>
        </DialogTitle>
        <DialogContent>
          {traceDetails && (
            <Box sx={{ pt: 2 }}>
              {/* Basic Info */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Message ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {traceDetails.message_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Campaign ID</Typography>
                      <Typography variant="body2">{traceDetails.campaign_id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">MSISDN</Typography>
                      <Typography variant="body2">
                        {traceDetails.msisdn.substring(0, 6)}****{traceDetails.msisdn.slice(-2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Entity ID</Typography>
                      <Typography variant="body2">{traceDetails.entity_id}</Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Timeline */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Message Timeline</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Timeline>
                    {traceDetails.timeline.map((event, index) => (
                      <TimelineItem key={index}>
                        <TimelineSeparator>
                          <TimelineDot color={getTimelineColor(event.status)}>
                            {getTimelineIcon(event.eventName, event.status)}
                          </TimelineDot>
                          {index < traceDetails.timeline.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">
                              {event.eventName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(event.timestamp).toLocaleString()}
                            </Typography>
                            {event.duration > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Duration: {formatDuration(event.duration)}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1 }}>
                              {Object.entries(event.details).map(([key, value]) => (
                                <Typography key={key} variant="body2">
                                  <strong>{key}:</strong> {String(value)}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </AccordionDetails>
              </Accordion>

              {/* Raw Payload */}
              {traceDetails.masked_payload && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Raw Payload (Masked)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <pre style={{ margin: 0, fontSize: '12px' }}>
                        {JSON.stringify(JSON.parse(traceDetails.masked_payload), null, 2)}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTraceDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageTrace;
